import React, { useEffect, useState } from 'react';
import useEditorStore from '../store/useEditorStore';
import { shallow } from 'zustand/shallow';
import { fabric } from 'fabric';
import { applyStrokeSettings, getLogicalStrokeWidth } from '../utils/strokeUtils';
import {
  FaAlignLeft,
  FaAlignCenter,
  FaAlignRight,
  FaImage,
  FaCopy,
  FaPaste
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { fontFamilies, getAvailableWeights, getWeightName, loadFonts } from '../utils/fontLoader';

const fontSizes = [
  { name: 'Extra Small', value: 12 },
  { name: 'Small', value: 14 },
  { name: 'Base', value: 16 },
  { name: 'Large', value: 18 },
  { name: 'Extra Large', value: 20 },
  { name: '2XL', value: 24 },
  { name: '3XL', value: 30 },
  { name: '4XL', value: 36 },
  { name: '5XL', value: 48 },
  { name: '6XL', value: 60 },
  { name: 'Custom...', value: 'custom' }
];



// Slider styles as a reusable className
const sliderClass = "w-full h-1 rounded-full appearance-none cursor-pointer bg-gray-200 dark:bg-gray-700 accent-primary [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:shadow-sm [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:transition-transform [&::-webkit-slider-thumb]:hover:scale-110 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-primary [&::-moz-range-thumb]:border-none [&::-moz-range-thumb]:cursor-pointer";

// Helper to get display name for object type
const getObjectTypeLabel = (obj: fabric.Object | null): string => {
  if (!obj) return 'Properties';
  if (obj.type === 'i-text' || obj.type === 'textbox') return 'Text Properties';
  if (obj.type === 'image' && obj.name === 'emoji') return 'Emoji Properties';
  if (obj.type === 'image') return 'Image Properties';
  if (obj.type === 'group' && (obj as any).svgData) return 'SVG Properties';
  if (obj.type === 'rect') return 'Rectangle Properties';
  if (obj.type === 'circle' || obj.type === 'ellipse') return 'Circle Properties';
  if (obj.type === 'line') return 'Line Properties';
  if (obj.type === 'polygon' || obj.type === 'triangle') return 'Shape Properties';
  if (obj.type === 'path') return 'Path Properties';
  return 'Properties';
};

const PropertiesPanel: React.FC = () => {
  // Use shallow selectors to avoid re-renders when unrelated store values change
  const {
    canvas: storeCanvas,
    selectedObject,
    artboardBackgroundImage,
    artboardBackgroundColor,
    isSaving,
    copiedTextProperties,
  } = useEditorStore(
    (state) => ({
      canvas: state.canvas,
      selectedObject: state.selectedObject,
      artboardBackgroundImage: state.artboardBackgroundImage,
      artboardBackgroundColor: state.artboardBackgroundColor,
      isSaving: state.isSaving,
      copiedTextProperties: state.copiedTextProperties,
    }),
    shallow
  );

  // Actions don't change identity, safe to select without shallow
  const setSelectedObject = useEditorStore((s) => s.setSelectedObject);
  const setArtboardWidth = useEditorStore((s) => s.setArtboardWidth);
  const setArtboardHeight = useEditorStore((s) => s.setArtboardHeight);
  const setArtboardBackgroundColor = useEditorStore((s) => s.setArtboardBackgroundColor);
  const setArtboardBackgroundImage = useEditorStore((s) => s.setArtboardBackgroundImage);
  const setArtboardName = useEditorStore((s) => s.setArtboardName);
  const setIsSaving = useEditorStore((s) => s.setIsSaving);
  const copyTextProperties = useEditorStore((s) => s.copyTextProperties);
  const pasteTextProperties = useEditorStore((s) => s.pasteTextProperties);
  const resetArtboardSettings = useEditorStore((s) => s.resetArtboardSettings);
  const setShapeSettings = useEditorStore((s) => s.setShapeSettings);

  // Use the canvas from the store directly to ensure synchronization
  const canvas = storeCanvas;
  const [skewX, setSkewX] = useState(0);
  const [skewY, setSkewY] = useState(0);
  const [textColor, setTextColor] = useState('#000000');
  const [fontSize, setFontSize] = useState(16);
  const [fontWeight, setFontWeight] = useState(400);
  const [fontFamily, setFontFamily] = useState('Roboto');
  const [lineHeight, setLineHeight] = useState(1.16);
  const [textAlign, setTextAlign] = useState<'left' | 'center' | 'right'>('left');
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const backgroundImageInputRef = React.useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [svgColor, setSvgColor] = useState('#000000');
  const [shapeFillEnabled, setShapeFillEnabled] = useState(true);
  const [shapeFillColor, setShapeFillColor] = useState('#4285f4');
  const [shapeStrokeEnabled, setShapeStrokeEnabled] = useState(true);
  const [shapeStrokeColor, setShapeStrokeColor] = useState('#000000');
  const [shapeStrokeWidth, setShapeStrokeWidth] = useState(2);
  const [fontsLoaded, setFontsLoaded] = useState(false);
  const [canvasReady, setCanvasReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [copyButtonText, setCopyButtonText] = useState('Copy');
  const [objWidth, setObjWidth] = useState(0);
  const [objHeight, setObjHeight] = useState(0);
  const [lockAspectRatio, setLockAspectRatio] = useState(true);

  // Check if everything is loaded
  useEffect(() => {
    if (fontsLoaded && canvasReady) {
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [fontsLoaded, canvasReady]);

  // Preset colors for quick selection
  const presetColors = [
    '#000000', '#ffffff', '#ff0000', '#00ff00', '#0000ff',
    '#ffff00', '#ff00ff', '#00ffff', '#ff8800', '#8800ff',
    '#0088ff', '#ff0088', '#88ff00', '#00ff88', '#880000',
    '#008800', '#000088', '#888888', '#cccccc', '#444444'
  ];

  // Check if canvas is ready for operations
  useEffect(() => {
    if (canvas && typeof canvas.getContext === 'function' && typeof canvas.getObjects === 'function') {
      setCanvasReady(true);
    } else {
      const timer = setTimeout(() => {
        if (canvas && typeof canvas.getContext === 'function' && typeof canvas.getObjects === 'function') {
          setCanvasReady(true);
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [canvas]);

  // Preload fonts
  useEffect(() => {
    const preloadFonts = async () => {
      try {
        await loadFonts();
        setFontsLoaded(true);

        window.dispatchEvent(new CustomEvent('fontsReady', {
          detail: { ready: true }
        }));
      } catch (error) {
        console.error('Error preloading fonts:', error);
        setFontsLoaded(true);

        window.dispatchEvent(new CustomEvent('fontsReady', {
          detail: { ready: true }
        }));
      }
    };

    preloadFonts();
  }, []);

  // Fetch artboard data when editing an existing artboard
  useEffect(() => {

    const fetchArtboardData = async () => {
      const artboardId = window.location.pathname.split('/').pop();
      const isNewArtboard = artboardId === 'editor' || !artboardId;

      if (isNewArtboard) {
        resetArtboardSettings();
        return;
      }

      if (!artboardId) {
        resetArtboardSettings();
        return;
      }

      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setArtboardName('Untitled Artboard');
          return;
        }

        const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001';
        const response = await fetch(`${API_URL}/api/artboards/${artboardId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          setArtboardName('Untitled Artboard');
          return;
        }

        const data = await response.json();
        setArtboardName(data.name || 'Untitled Artboard');

        if (data.data) {
          if (data.data.width && data.data.height) {
            setArtboardWidth(data.data.width);
            setArtboardHeight(data.data.height);
          }

          if (data.data.backgroundColor) {
            setArtboardBackgroundColor(data.data.backgroundColor);
            if (canvas) {
              canvas.setBackgroundColor(data.data.backgroundColor, () => {
                canvas.renderAll();
              });
            } else {
              localStorage.setItem('savedArtboardBackgroundColor', data.data.backgroundColor);
            }
          }

          const bgImageData = data.data.backgroundImage;
          if (bgImageData) {
            const src = typeof bgImageData === 'string' ? bgImageData : bgImageData.src;
            setArtboardBackgroundImage(src || null);
          } else {
            setArtboardBackgroundImage(null);
          }
        }
      } catch (error) {
        console.error('Error fetching artboard data:', error);
        setArtboardName('Untitled Artboard');
      }
    };

    fetchArtboardData();
  }, []);

  // Handle canvas-dependent operations when canvas becomes available
  useEffect(() => {
    if (!canvas) return;

    const savedBackgroundColor = localStorage.getItem('savedArtboardBackgroundColor');
    if (savedBackgroundColor) {
      canvas.setBackgroundColor(savedBackgroundColor, () => {
        canvas.renderAll();
      });
      localStorage.removeItem('savedArtboardBackgroundColor');
    }
  }, [canvas]);

  // Type guard for IText objects
  const isIText = (obj: fabric.Object): obj is fabric.IText => {
    return obj.type === 'i-text' || obj.type === 'textbox';
  };

  // Type guard for shape objects
  const isShape = (obj: fabric.Object): boolean => {
    return obj.type === 'rect' ||
           obj.type === 'circle' ||
           obj.type === 'ellipse' ||
           obj.type === 'line' ||
           obj.type === 'polygon' ||
           obj.type === 'triangle' ||
           obj.type === 'path';
  };

  const getObjDisplaySize = (obj: fabric.Object) => {
    const w = (obj.width || 0) * (obj.scaleX || 1);
    const h = (obj.height || 0) * (obj.scaleY || 1);
    return { w: Math.round(w), h: Math.round(h) };
  };

  const updateObjDimensions = (obj: fabric.Object | null) => {
    if (!obj) return;
    const { w, h } = getObjDisplaySize(obj);
    setObjWidth(w);
    setObjHeight(h);
  };

  useEffect(() => {
    if (!canvas) return;

    const handleSelection = () => {
      const activeObject = canvas.getActiveObject();
      setSelectedObject(activeObject);
      if (activeObject) {
        updateObjDimensions(activeObject);
        setSkewX(activeObject.skewX || 0);
        setSkewY(activeObject.skewY || 0);
        if (isIText(activeObject)) {
          setTextColor(activeObject.fill as string);
          setFontSize(activeObject.fontSize || 16);
          const currentWeight = typeof activeObject.fontWeight === 'string'
            ? parseInt(activeObject.fontWeight, 10)
            : (activeObject.fontWeight as number) || 400;
          setFontWeight(currentWeight);
          setFontFamily(activeObject.fontFamily || 'Roboto');
          setLineHeight(activeObject.lineHeight || 1.16);
          setTextAlign((activeObject.textAlign as 'left' | 'center' | 'right') || 'left');
        }
        if (isShape(activeObject)) {
          const fill = activeObject.fill as string;
          const stroke = activeObject.stroke as string;
          const strokeWidth = getLogicalStrokeWidth(activeObject);
          setShapeFillEnabled(fill !== 'transparent' && fill !== '');
          setShapeFillColor(fill && fill !== 'transparent' ? fill : '#4285f4');
          setShapeStrokeEnabled(stroke !== '' && strokeWidth > 0);
          setShapeStrokeColor(stroke || '#000000');
          setShapeStrokeWidth(strokeWidth || 2);
        }
      }
    };

    const handleModified = (e: any) => {
      if (e.target) updateObjDimensions(e.target);
    };
    const handleScaling = (e: any) => {
      if (e.target) updateObjDimensions(e.target);
    };

    canvas.on('selection:created', handleSelection);
    canvas.on('selection:updated', handleSelection);
    canvas.on('object:modified', handleModified);
    canvas.on('object:scaling', handleScaling);
    canvas.on('selection:cleared', () => {
      setSelectedObject(null);
      setObjWidth(0);
      setObjHeight(0);
      setSkewX(0);
      setSkewY(0);
      setTextColor('#000000');
      setFontSize(16);
      setFontWeight(400);
      setFontFamily('Roboto');
      setLineHeight(1.16);
      setTextAlign('left');
      setShapeFillEnabled(true);
      setShapeFillColor('#4285f4');
      setShapeStrokeEnabled(true);
      setShapeStrokeColor('#000000');
      setShapeStrokeWidth(2);
    });

    return () => {
      canvas.off('selection:created', handleSelection);
      canvas.off('selection:updated', handleSelection);
      canvas.off('object:modified', handleModified);
      canvas.off('object:scaling', handleScaling);
      canvas.off('selection:cleared');
    };
  }, [canvas]);

  // Add effect to update SVG color when selected object changes
  useEffect(() => {
    if (selectedObject && selectedObject.type === 'group' && (selectedObject as any).svgData) {
      setSvgColor(selectedObject.fill as string || '#000000');
    }
  }, [selectedObject]);

  // Add keyboard shortcuts for copy/paste transform properties
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!selectedObject || (!isIText(selectedObject) && !(selectedObject.type === 'image' && selectedObject.name === 'emoji'))) return;

      if (e.ctrlKey || e.metaKey) {
        if (e.key === 'c') {
          e.preventDefault();
          handleCopyProperties();
        } else if (e.key === 'v' && copiedTextProperties) {
          e.preventDefault();
          handlePasteProperties();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedObject, copiedTextProperties]);

  // Listen for save event from header
  useEffect(() => {
    const handleSaveFromHeader = () => {
      setTimeout(() => {
        handleSave();
      }, 50);
    };

    window.addEventListener('saveArtboard', handleSaveFromHeader);
    return () => {
      window.removeEventListener('saveArtboard', handleSaveFromHeader);
    };
  }, []);

  // Add handler for SVG color changes
  const handleSvgColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedObject || !canvas) return;

    const newColor = e.target.value;
    setSvgColor(newColor);

    selectedObject.set({
      fill: newColor,
      stroke: newColor
    });

    canvas.renderAll();
  };

  // Handlers for shape properties
  const handleShapeFillToggle = () => {
    if (!selectedObject || !canvas || !isShape(selectedObject)) return;

    const newFillEnabled = !shapeFillEnabled;
    setShapeFillEnabled(newFillEnabled);

    selectedObject.set({
      fill: newFillEnabled ? shapeFillColor : 'transparent'
    });

    canvas.renderAll();
  };

  const handleShapeFillColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedObject || !canvas || !isShape(selectedObject)) return;

    const newColor = e.target.value;
    setShapeFillColor(newColor);
    setShapeSettings({ fillColor: newColor });

    if (shapeFillEnabled) {
      selectedObject.set({ fill: newColor });
      canvas.renderAll();
    }
  };

  const handleShapeFillColorValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.trim();
    if (value && !value.startsWith('#')) {
      value = '#' + value;
    }
    setShapeFillColor(value);
  };

  const handleShapeFillColorValueBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value.trim();
    const hexColorRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;

    if (hexColorRegex.test(value)) {
      setShapeFillColor(value);
      setShapeSettings({ fillColor: value });
      if (selectedObject && canvas && isShape(selectedObject) && shapeFillEnabled) {
        selectedObject.set({ fill: value });
        canvas.renderAll();
      }
    } else {
      if (selectedObject && isShape(selectedObject)) {
        const currentFill = selectedObject.fill as string;
        const validColor = currentFill && currentFill !== 'transparent' ? currentFill : '#ffffff';
        setShapeFillColor(validColor);
      } else {
        setShapeFillColor('#ffffff');
      }
    }
  };

  const handleShapeStrokeToggle = () => {
    if (!selectedObject || !canvas || !isShape(selectedObject)) return;

    const newStrokeEnabled = !shapeStrokeEnabled;
    setShapeStrokeEnabled(newStrokeEnabled);

    if (newStrokeEnabled) {
      applyStrokeSettings(selectedObject, shapeStrokeColor, shapeStrokeWidth);
    } else {
      applyStrokeSettings(selectedObject, '', 0);
    }

    canvas.renderAll();
  };

  const handleShapeStrokeColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedObject || !canvas || !isShape(selectedObject)) return;

    const newColor = e.target.value;
    setShapeStrokeColor(newColor);
    setShapeSettings({ strokeColor: newColor });

    if (shapeStrokeEnabled) {
      const strokeWidth = shapeStrokeWidth || 0;
      applyStrokeSettings(selectedObject, newColor, strokeWidth);
      canvas.renderAll();
    }
  };

  const handleShapeStrokeColorValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.trim();
    if (value && !value.startsWith('#')) {
      value = '#' + value;
    }
    setShapeStrokeColor(value);
  };

  const handleShapeStrokeColorValueBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value.trim();
    const hexColorRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;

    if (hexColorRegex.test(value)) {
      setShapeStrokeColor(value);
      setShapeSettings({ strokeColor: value });
      if (selectedObject && canvas && isShape(selectedObject) && shapeStrokeEnabled) {
        const strokeWidth = shapeStrokeWidth || 0;
        applyStrokeSettings(selectedObject, value, strokeWidth);
        canvas.renderAll();
      }
    } else {
      if (selectedObject && isShape(selectedObject)) {
        const currentStroke = selectedObject.stroke as string;
        const validColor = currentStroke || '#000000';
        setShapeStrokeColor(validColor);
      } else {
        setShapeStrokeColor('#000000');
      }
    }
  };

  const handleShapeStrokeWidthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedObject || !canvas || !isShape(selectedObject)) return;

    const newWidth = parseInt(e.target.value, 10);
    setShapeStrokeWidth(newWidth);
    setShapeSettings({ strokeWidth: newWidth });

    if (shapeStrokeEnabled) {
      const strokeColor = shapeStrokeColor || '#000000';
      applyStrokeSettings(selectedObject, strokeColor, newWidth);
      canvas.renderAll();
    }
  };

  const handlePropertyChange = async (property: keyof fabric.IText, value: string | number | boolean) => {
    if (!selectedObject || !isIText(selectedObject) || !canvas) return;

    selectedObject.set(property, value);

    if (property === 'fontFamily' || property === 'fontWeight') {
      const fontFamily = property === 'fontFamily' ? value as string : selectedObject.fontFamily;
      const fontWeight = property === 'fontWeight' ? value as string : selectedObject.fontWeight;

      if ('fonts' in document) {
        try {
          await document.fonts.load(`${fontWeight} 16px ${fontFamily}`);
        } catch (error) {
          console.warn(`Failed to load font: ${fontWeight} ${fontFamily}`, error);
        }
      }

      selectedObject.initDimensions();
      selectedObject.setCoords();
      canvas.renderAll();

      setTimeout(() => {
        if (canvas && selectedObject) {
          selectedObject.initDimensions();
          selectedObject.setCoords();
          canvas.renderAll();
        }
      }, 100);

      setTimeout(() => {
        if (canvas && selectedObject) {
          canvas.renderAll();
        }
      }, 200);
    } else if (property === 'fontSize' || property === 'lineHeight') {
      selectedObject.initDimensions();
      selectedObject.setCoords();
      canvas.renderAll();
    } else {
      canvas.renderAll();
    }
  };

  const handleCopyProperties = () => {
    if (!selectedObject || (!isIText(selectedObject) && !(selectedObject.type === 'image' && selectedObject.name === 'emoji'))) return;
    copyTextProperties(selectedObject as fabric.IText);
    setToast({ message: 'Rotation and skew copied!', type: 'success' });

    setCopyButtonText('Copied!');
    setTimeout(() => {
      setCopyButtonText('Copy');
    }, 1500);
  };

  const handlePasteProperties = () => {
    if (!selectedObject || (!isIText(selectedObject) && !(selectedObject.type === 'image' && selectedObject.name === 'emoji')) || !copiedTextProperties) return;
    pasteTextProperties(selectedObject as fabric.IText);

    setSkewX(copiedTextProperties.skewX);
    setSkewY(copiedTextProperties.skewY);

    setToast({ message: 'Rotation and skew applied!', type: 'success' });
  };

  const handleSkewXChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedObject || !canvas) return;
    const value = parseFloat(e.target.value);
    setSkewX(value);
    selectedObject.set('skewX', value);
    canvas.renderAll();
  };

  const handleSkewYChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedObject || !canvas) return;
    const value = parseFloat(e.target.value);
    setSkewY(value);
    selectedObject.set('skewY', value);
    canvas.renderAll();
  };

  const handleChangeImage = () => {
    fileInputRef.current?.click();
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedObject || !canvas) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const imgUrl = event.target?.result as string;

      const tempImg = new Image();
      tempImg.onerror = () => {
        console.error('Failed to load image. Format may not be supported:', file.type);
        alert('Failed to load image. This format may not be fully supported. Please try PNG, JPG, or WebP.');
      };
      tempImg.onload = () => {
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = tempImg.width;
        tempCanvas.height = tempImg.height;
        const ctx = tempCanvas.getContext('2d');

        if (!ctx) {
          console.error('Could not get canvas context');
          return;
        }

        ctx.drawImage(tempImg, 0, 0);
        const pngDataUrl = tempCanvas.toDataURL('image/png');

        fabric.Image.fromURL(pngDataUrl, (img) => {
          const currentObject = selectedObject as fabric.Image;
          img.set({
            left: currentObject.left,
            top: currentObject.top,
            scaleX: currentObject.scaleX,
            scaleY: currentObject.scaleY,
            angle: currentObject.angle,
            skewX: currentObject.skewX,
            skewY: currentObject.skewY,
            flipX: currentObject.flipX,
            flipY: currentObject.flipY,
            selectable: true,
            hasControls: true,
            hasBorders: true,
            cornerStyle: 'circle',
            cornerSize: 8,
            transparentCorners: false,
            cornerColor: '#4285f4',
            cornerStrokeColor: '#ffffff',
            borderColor: '#4285f4',
            borderScaleFactor: 1,
            padding: 8,
            crossOrigin: 'anonymous'
          });

          (img as any).dataUrl = pngDataUrl;

          const index = canvas.getObjects().indexOf(currentObject);
          canvas.remove(currentObject);
          canvas.insertAt(img, index, false);
          canvas.setActiveObject(img);
          setSelectedObject(img);
          canvas.renderAll();

        }, { crossOrigin: 'anonymous' });
      };

      tempImg.onerror = (error) => {
        console.error('Error loading image:', error);
        setToast({
          message: 'Error loading image. Please try a different format.',
          type: 'error'
        });
      };

      tempImg.src = imgUrl;
    };

    reader.onerror = (error) => {
      console.error('Error reading file:', error);
      setToast({
        message: 'Error reading image file. Please try again.',
        type: 'error'
      });
    };

    reader.readAsDataURL(file);
  };

  const handleBackgroundImageSelect = () => {
    backgroundImageInputRef.current?.click();
  };

  const handleBackgroundImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const supportedTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/svg+xml', 'image/bmp', 'image/avif', 'image/tiff'];
    if (!supportedTypes.includes(file.type) && !file.type.startsWith('image/')) {
      setToast({ message: 'Unsupported image format', type: 'error' });
      return;
    }

    // Validate file size (max 20MB)
    if (file.size > 20 * 1024 * 1024) {
      setToast({ message: 'Image too large. Please use an image under 20MB.', type: 'error' });
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const imgUrl = event.target?.result as string;
      // Validate the image actually loads in the browser
      const testImg = new Image();
      testImg.onload = () => {
        setArtboardBackgroundImage(imgUrl);
      };
      testImg.onerror = () => {
        setToast({ message: 'Could not load image. The format may not be supported by your browser.', type: 'error' });
      };
      testImg.src = imgUrl;
    };
    reader.onerror = () => {
      setToast({ message: 'Error reading background image', type: 'error' });
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveBackgroundImage = () => {
    setArtboardBackgroundImage(null);
  };

  const handleWidthChange = (newWidth: number) => {
    if (!selectedObject || !canvas || newWidth <= 0) return;
    const origW = selectedObject.width || 1;
    const origH = selectedObject.height || 1;
    const newScaleX = newWidth / origW;

    if (lockAspectRatio) {
      const currentScaleX = selectedObject.scaleX || 1;
      const currentScaleY = selectedObject.scaleY || 1;
      const ratio = currentScaleY / currentScaleX;
      const newScaleY = newScaleX * ratio;
      selectedObject.set({ scaleX: newScaleX, scaleY: newScaleY });
      setObjWidth(newWidth);
      setObjHeight(Math.round(origH * newScaleY));
    } else {
      selectedObject.set({ scaleX: newScaleX });
      setObjWidth(newWidth);
    }
    selectedObject.setCoords();
    canvas.renderAll();
  };

  const handleHeightChange = (newHeight: number) => {
    if (!selectedObject || !canvas || newHeight <= 0) return;
    const origW = selectedObject.width || 1;
    const origH = selectedObject.height || 1;
    const newScaleY = newHeight / origH;

    if (lockAspectRatio) {
      const currentScaleX = selectedObject.scaleX || 1;
      const currentScaleY = selectedObject.scaleY || 1;
      const ratio = currentScaleX / currentScaleY;
      const newScaleX = newScaleY * ratio;
      selectedObject.set({ scaleX: newScaleX, scaleY: newScaleY });
      setObjWidth(Math.round(origW * newScaleX));
      setObjHeight(newHeight);
    } else {
      selectedObject.set({ scaleY: newScaleY });
      setObjHeight(newHeight);
    }
    selectedObject.setCoords();
    canvas.renderAll();
  };

  const handleSave = async () => {

    if (isSaving) return;
    setIsSaving(true);

    const canvasFromStore = useEditorStore.getState().canvas;
    const canvasFromSubscription = canvas;
    const canvasFromLocal = canvas;

    let canvasToUse = canvasFromStore || canvasFromSubscription || canvasFromLocal;

    if (!canvasToUse) {
      setToast({ message: 'Canvas not available for saving', type: 'error' });
      setIsSaving(false);
      return;
    }

    const canvasReady = typeof canvasToUse.getContext === 'function' && typeof canvasToUse.getObjects === 'function';
    if (!canvasReady) {
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setToast({ message: 'Please log in to save', type: 'error' });
        return;
      }

      let bgImageSrc =
        artboardBackgroundImage ||
        (canvasToUse.backgroundImage && (canvasToUse.backgroundImage as any).getSrc?.()) ||
        (canvasToUse.backgroundImage as any)?._originalElement?.src ||
        (canvasToUse.backgroundImage as any)?.src;
      if (!bgImageSrc && canvasToUse.backgroundImage && (canvasToUse.backgroundImage as any).toDataURL) {
        try {
          bgImageSrc = (canvasToUse.backgroundImage as any).toDataURL({ format: 'png' });
        } catch {
          // Ignore background image serialization errors
        }
      }

      const canvasData = {
        version: '5.2.1',
        width: canvasToUse.width,
        height: canvasToUse.height,
        backgroundColor: canvasToUse.backgroundColor as string,
        backgroundImage: bgImageSrc ? { src: bgImageSrc } : null,
        objects: canvasToUse.getObjects().map(obj => {
          const propertiesToInclude = ['selectable', 'hasControls', 'hasBorders', 'cornerStyle', 'cornerSize', 'transparentCorners', 'cornerColor', 'cornerStrokeColor', 'borderColor', 'borderScaleFactor', 'padding', 'perspectiveEnabled', 'perspectiveCorners'];
          if (obj.type === 'group' && (obj as any).svgData) {
            propertiesToInclude.push('svgData');
          }
          const objData = obj.toObject(propertiesToInclude);

          if ((obj as any).perspectiveEnabled && (obj as any).perspectiveCorners) {
            (objData as any).perspectiveEnabled = true;
            (objData as any).perspectiveCorners = (obj as any).perspectiveCorners;
          }

          if (obj.type === 'group' && (obj as any).svgData) {
            (objData as any).svgData = (obj as any).svgData;
          }

          if (obj.type === 'image') {
            const imgObj = obj as fabric.Image;
            const src = (imgObj as any).dataUrl || (imgObj as any).src;
            if (src) {
              (objData as any).src = src;
            }
            // Remove duplicate dataUrl to halve image payload size
            delete (objData as any).dataUrl;
          }

          return objData;
        })
      };

      // Generate preview thumbnail
      canvasToUse.renderAll();
      const preview = canvasToUse.toDataURL({
        format: 'png',
        quality: 0.7,
        multiplier: 0.5,
        enableRetinaScaling: false
      });

      if (!preview || !preview.startsWith('data:image/png;base64,')) {
        setToast({ message: 'Error generating preview', type: 'error' });
        return;
      }

      const artboardId = window.location.pathname.split('/').pop();
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001';

      const isNewArtboard = artboardId === 'editor' || !artboardId;
      const url = isNewArtboard
        ? `${API_URL}/api/artboards`
        : `${API_URL}/api/artboards/${artboardId}`;

      const method = isNewArtboard ? 'POST' : 'PUT';

      const currentArtboardName = useEditorStore.getState().artboardName;

      const requestBody = {
        name: currentArtboardName,
        data: canvasData,
        preview
      };

      // Serialize once and reuse for both size check and request
      const bodyJson = JSON.stringify(requestBody);
      if (bodyJson.length > 50 * 1024 * 1024) {
        setToast({
          message: 'Artboard is too large to save. Please reduce the size of images or remove some objects.',
          type: 'error'
        });
        return;
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: bodyJson,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      let responseData;
      try {
        responseData = await response.json();
      } catch (parseError) {
        console.error('Error parsing response:', parseError);
        const responseText = await response.text();
        throw new Error(`Failed to parse response: ${responseText}`);
      }

      if (!response.ok) {
        throw new Error(responseData.message || `Failed to save artboard (${response.status})`);
      }

      setToast({ message: 'Artboard saved successfully', type: 'success' });

      if (isNewArtboard) {
        navigate(`/editor/${responseData._id}`);
      }
    } catch (error) {
      console.error('Error saving artboard:', error);

      let errorMessage = 'Error saving artboard';
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          errorMessage = 'Save request timed out. Please try again.';
        } else if (error.message.includes('Failed to fetch')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        } else if (error.message.includes('401')) {
          errorMessage = 'Authentication expired. Please log in again.';
        } else if (error.message.includes('500')) {
          errorMessage = 'Server error. Please try again later.';
        } else {
          errorMessage = error.message || 'Unknown error occurred';
        }
      }

      setToast({
        message: errorMessage,
        type: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Add useEffect to handle toast timeout
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => {
        setToast(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // --- RENDER ---

  if (isLoading) {
    return (
      <div className="h-full overflow-y-auto overflow-x-hidden bg-white dark:bg-gray-900 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-200 hover:[&::-webkit-scrollbar-thumb]:bg-gray-300 dark:[&::-webkit-scrollbar-thumb]:bg-gray-700 dark:hover:[&::-webkit-scrollbar-thumb]:bg-gray-600 md:border-l-0">
        <div className="sticky top-0 z-10 border-b border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-900">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
            <span className="material-symbols-rounded text-[16px] text-gray-400">tune</span>
            Properties
          </h3>
        </div>
        <div className="flex items-center justify-center gap-2 px-4 py-8 text-sm text-gray-400 dark:text-gray-500">
          Loading properties...
        </div>
      </div>
    );
  }

  if (!selectedObject) {
    return (
      <>
        <div className="h-full overflow-y-auto overflow-x-hidden bg-white dark:bg-gray-900 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-200 hover:[&::-webkit-scrollbar-thumb]:bg-gray-300 dark:[&::-webkit-scrollbar-thumb]:bg-gray-700 dark:hover:[&::-webkit-scrollbar-thumb]:bg-gray-600 md:border-l-0">
          <div className="sticky top-0 z-10 border-b border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-900">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
              <span className="material-symbols-rounded text-[16px] text-gray-400">tune</span>
              Properties
            </h3>
          </div>

        {/* Artboard Background */}
        <div className="border-b border-gray-100 dark:border-gray-800">
          <div className="px-4 py-3">
            <div className="mb-2 text-[11px] font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">Artboard</div>
            <div className="mb-2 text-xs text-gray-500 dark:text-gray-400">Background Image (Cover)</div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleBackgroundImageSelect}
                className="flex flex-1 items-center justify-center gap-1 rounded-lg border border-gray-200 bg-gray-50 px-2 py-1.5 text-[11px] font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                <span className="material-symbols-rounded text-[14px]">image</span>
                {artboardBackgroundImage ? 'Change Background' : 'Upload Background'}
              </button>
              {artboardBackgroundImage && (
                <button
                  onClick={handleRemoveBackgroundImage}
                  className="flex items-center justify-center gap-1 rounded-lg border border-red-200 bg-red-50 px-2 py-1.5 text-[11px] font-medium text-red-600 transition-colors hover:bg-red-100 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30"
                >
                  <span className="material-symbols-rounded text-[14px]">delete</span>
                  Remove
                </button>
              )}
            </div>
            {artboardBackgroundImage && (
              <img
                src={artboardBackgroundImage}
                alt="Background"
                className="mt-2 w-full rounded-lg border border-gray-200 object-contain dark:border-gray-700"
                style={{ maxHeight: '160px' }}
              />
            )}
            <input
              ref={backgroundImageInputRef}
              type="file"
              accept="image/*"
              onChange={handleBackgroundImageChange}
              className="hidden"
            />
          </div>
        </div>

          <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
            <span className="material-symbols-rounded mb-3 text-[40px] text-gray-300 dark:text-gray-600">touch_app</span>
            <div className="mb-1 text-sm font-medium text-gray-900 dark:text-white">No Object Selected</div>
            <div className="text-xs leading-relaxed text-gray-400 dark:text-gray-500">
              Select an object on the canvas to view and edit its properties
            </div>
          </div>
        </div>
        {/* Toast */}
        <div className="fixed right-5 top-5 z-[1000]">
          {toast && (
            <div className={`flex items-center gap-2 rounded-lg px-4 py-3 text-sm font-medium text-white shadow-lg animate-[slideIn_0.3s_ease] ${toast.type === 'success' ? 'bg-green-600' : 'bg-red-500'}`}>
              {toast.type === 'success' ? <span className="material-symbols-rounded text-[16px]">check_circle</span> : <span className="material-symbols-rounded text-[16px]">error</span>} {toast.message}
            </div>
          )}
        </div>
      </>
    );
  }

  return (
    <>
      <div className="h-full overflow-y-auto overflow-x-hidden bg-white dark:bg-gray-900 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-200 hover:[&::-webkit-scrollbar-thumb]:bg-gray-300 dark:[&::-webkit-scrollbar-thumb]:bg-gray-700 dark:hover:[&::-webkit-scrollbar-thumb]:bg-gray-600 md:border-l-0">
        <div className="sticky top-0 z-10 border-b border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-900">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
            <span className="material-symbols-rounded text-[16px] text-gray-400">tune</span>
            {getObjectTypeLabel(selectedObject)}
          </h3>
        </div>

        {/* Artboard Background */}
        <div className="border-b border-gray-100 dark:border-gray-800">
          <div className="px-4 py-3">
            <div className="mb-2 text-[11px] font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">Artboard</div>
            <div className="mb-2 text-xs text-gray-500 dark:text-gray-400">Background Image (Cover)</div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleBackgroundImageSelect}
                className="flex flex-1 items-center justify-center gap-1 rounded-lg border border-gray-200 bg-gray-50 px-2 py-1.5 text-[11px] font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                <span className="material-symbols-rounded text-[14px]">image</span>
                {artboardBackgroundImage ? 'Change Background' : 'Upload Background'}
              </button>
              {artboardBackgroundImage && (
                <button
                  onClick={handleRemoveBackgroundImage}
                  className="flex items-center justify-center gap-1 rounded-lg border border-red-200 bg-red-50 px-2 py-1.5 text-[11px] font-medium text-red-600 transition-colors hover:bg-red-100 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30"
                >
                  <span className="material-symbols-rounded text-[14px]">delete</span>
                  Remove
                </button>
              )}
            </div>
            {artboardBackgroundImage && (
              <img
                src={artboardBackgroundImage}
                alt="Background"
                className="mt-2 w-full rounded-lg border border-gray-200 object-contain dark:border-gray-700"
                style={{ maxHeight: '160px' }}
              />
            )}
            <input
              ref={backgroundImageInputRef}
              type="file"
              accept="image/*"
              onChange={handleBackgroundImageChange}
              className="hidden"
            />
          </div>
        </div>

        {/* Dimensions - Available for all objects */}
        <div className="border-b border-gray-100 dark:border-gray-800">
          <div className="px-4 py-3">
            <div className="mb-2 text-[11px] font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">Dimensions</div>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">W</div>
                <input
                  type="number"
                  min="1"
                  max="5000"
                  value={objWidth}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10);
                    if (!isNaN(val)) handleWidthChange(val);
                  }}
                  className="h-9 w-full rounded-lg border border-gray-200 bg-gray-50 px-2.5 text-sm text-gray-900 outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/10 dark:border-gray-700 dark:bg-gray-800 dark:text-white [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [-moz-appearance:textfield]"
                />
              </div>
              <button
                onClick={() => setLockAspectRatio(!lockAspectRatio)}
                title={lockAspectRatio ? 'Unlock aspect ratio' : 'Lock aspect ratio'}
                className={`mb-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border transition-colors ${lockAspectRatio ? 'border-primary/30 bg-primary/10 text-primary' : 'border-gray-200 bg-gray-50 text-gray-400 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-500 dark:hover:bg-gray-700'}`}
              >
                <span className="material-symbols-rounded text-[18px]">{lockAspectRatio ? 'link' : 'link_off'}</span>
              </button>
              <div className="flex-1">
                <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">H</div>
                <input
                  type="number"
                  min="1"
                  max="5000"
                  value={objHeight}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10);
                    if (!isNaN(val)) handleHeightChange(val);
                  }}
                  className="h-9 w-full rounded-lg border border-gray-200 bg-gray-50 px-2.5 text-sm text-gray-900 outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/10 dark:border-gray-700 dark:bg-gray-800 dark:text-white [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [-moz-appearance:textfield]"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Transform Properties - Available for both text and emoji objects */}
        {(isIText(selectedObject) || (selectedObject.type === 'image' && selectedObject.name === 'emoji')) && (
          <div className="border-b border-gray-100 dark:border-gray-800">
            <div className="px-4 py-3">
              <div className="mb-2 text-[11px] font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">Transform</div>
              <div className="mb-3">
                <div className="flex gap-2 mb-2">
                  <button
                    onClick={handleCopyProperties}
                    title="Copy rotation and skew (Ctrl+C)"
                    className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                  >
                    <FaCopy size={10} />
                    {copyButtonText}
                  </button>
                  {copiedTextProperties && (
                    <button
                      onClick={handlePasteProperties}
                      title={`Paste rotation and skew (Ctrl+V) - Angle: ${copiedTextProperties.angle}\u00B0, SkewX: ${copiedTextProperties.skewX}\u00B0, SkewY: ${copiedTextProperties.skewY}\u00B0`}
                      className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                    >
                      <FaPaste size={10} />
                      Paste
                    </button>
                  )}
                </div>
                {copiedTextProperties && (
                  <div className="mb-2 text-[11px] italic text-green-600 dark:text-green-400">
                    Transform copied: Angle {copiedTextProperties.angle}&deg;, SkewX {copiedTextProperties.skewX}&deg;, SkewY {copiedTextProperties.skewY}&deg;
                  </div>
                )}
              </div>
              <div className="mb-3">
                <div className="mb-2 text-[11px] font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">Skew</div>
                {/* Skew X */}
                <div className="mb-3">
                  <div className="mb-1 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                    <span>Skew X</span>
                    <div className="flex items-center gap-1.5">
                      <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-700 dark:bg-gray-800 dark:text-gray-300">{skewX.toFixed(1)}&deg;</span>
                      <button
                        onClick={() => {
                          setSkewX(0);
                          if (selectedObject) {
                            selectedObject.set('skewX', 0);
                            canvas?.renderAll();
                          }
                        }}
                        title="Reset to 0\u00B0"
                        className="flex h-5 w-5 items-center justify-center rounded text-[11px] text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-300"
                      >
                        <span className="material-symbols-rounded text-[14px]">restart_alt</span>
                      </button>
                    </div>
                  </div>
                  <div className="py-1">
                    <input
                      type="range"
                      min="-45"
                      max="45"
                      step="0.1"
                      value={skewX}
                      onChange={handleSkewXChange}
                      className={sliderClass}
                    />
                  </div>
                </div>
                {/* Skew Y */}
                <div className="mb-0">
                  <div className="mb-1 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                    <span>Skew Y</span>
                    <div className="flex items-center gap-1.5">
                      <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-700 dark:bg-gray-800 dark:text-gray-300">{skewY.toFixed(1)}&deg;</span>
                      <button
                        onClick={() => {
                          setSkewY(0);
                          if (selectedObject) {
                            selectedObject.set('skewY', 0);
                            canvas?.renderAll();
                          }
                        }}
                        title="Reset to 0\u00B0"
                        className="flex h-5 w-5 items-center justify-center rounded text-[11px] text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-300"
                      >
                        <span className="material-symbols-rounded text-[14px]">restart_alt</span>
                      </button>
                    </div>
                  </div>
                  <div className="py-1">
                    <input
                      type="range"
                      min="-45"
                      max="45"
                      step="0.1"
                      value={skewY}
                      onChange={handleSkewYChange}
                      className={sliderClass}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Text Properties - Only for text objects */}
        {isIText(selectedObject) && (
          <>
            <div className="border-b border-gray-100 dark:border-gray-800">
              <div className="px-4 py-3">
                <div className="mb-3 text-[11px] font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">Color</div>
                <div className="mb-3">
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <div
                        className="h-9 w-9 cursor-pointer rounded-lg border-2 border-gray-200 transition-all hover:scale-105 hover:border-primary dark:border-gray-700"
                        style={{ background: textColor }}
                        onClick={() => {
                          const colorInput = document.getElementById('textColorInput') as HTMLInputElement;
                          if (colorInput) colorInput.click();
                        }}
                      />
                      <input
                        id="textColorInput"
                        type="color"
                        value={textColor}
                        onChange={(e) => {
                          const newColor = e.target.value;
                          setTextColor(newColor);
                          handlePropertyChange('fill', newColor);
                        }}
                        className="pointer-events-none absolute left-0 top-0 h-0 w-0 opacity-0"
                      />
                    </div>
                    <span className="font-mono text-xs font-medium text-gray-700 dark:text-gray-300">{textColor.toUpperCase()}</span>
                  </div>
                  <div className="mt-2.5 flex flex-wrap gap-1.5">
                    {presetColors.map((color) => (
                      <button
                        key={color}
                        className={`h-5 w-5 cursor-pointer rounded transition-all hover:scale-110 ${textColor === color ? 'ring-2 ring-primary ring-offset-1 dark:ring-offset-gray-900' : 'border border-gray-200 dark:border-gray-700'}`}
                        style={{ background: color }}
                        onClick={() => {
                          setTextColor(color);
                          handlePropertyChange('fill', color);
                        }}
                        title={color.toUpperCase()}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="border-b border-gray-100 dark:border-gray-800">
              <div className="px-4 py-3">
                <div className="mb-3 text-[11px] font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">Typography</div>

                {/* Font Family */}
                <div className="mb-3">
                  <div className="mb-1 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                    <span>Font Family</span>
                    {!fontsLoaded && (
                      <span className="text-[10px] italic text-yellow-500">Loading...</span>
                    )}
                  </div>
                  <select
                    value={fontFamily}
                    onChange={async (e) => {
                      const value = e.target.value;
                      setFontFamily(value);
                      setFontWeight(400);
                      await handlePropertyChange('fontFamily', value);
                      await handlePropertyChange('fontWeight', 400);
                    }}
                    disabled={!fontsLoaded}
                    className="h-9 w-full cursor-pointer rounded-lg border border-gray-200 bg-gray-50 px-2.5 text-sm text-gray-900 outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/10 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                  >
                    {!fontsLoaded && (
                      <option value="">Loading fonts...</option>
                    )}
                    {fontFamilies.map(font => (
                      <option key={font.value} value={font.value} style={{ fontFamily: font.value }}>
                        {font.category} - {font.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Font Preview */}
                <div className="mb-3">
                  <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">Preview</div>
                  {!fontsLoaded ? (
                    <div className="flex items-center justify-center py-4 text-xs text-gray-400">Loading fonts...</div>
                  ) : (
                    <div
                      className="flex min-h-[52px] items-center rounded-lg border border-gray-200 p-3 text-sm transition-colors hover:border-primary/40 dark:border-gray-700"
                      style={{
                        fontFamily: fontFamily,
                        fontSize: '16px',
                        fontWeight: fontWeight,
                        lineHeight: lineHeight,
                        textAlign: textAlign,
                        color: textColor,
                        background: artboardBackgroundColor,
                        wordBreak: 'break-word'
                      }}
                    >
                      The quick brown fox jumps over the lazy dog
                    </div>
                  )}
                </div>

                {/* Font Size */}
                <div className="mb-3">
                  <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">Size</div>
                  <div className="flex gap-2">
                    <select
                      value={fontSize}
                      onChange={(e) => {
                        const value = parseInt(e.target.value);
                        setFontSize(value);
                        handlePropertyChange('fontSize', value);
                      }}
                      className="h-9 flex-1 cursor-pointer rounded-lg border border-gray-200 bg-gray-50 px-2.5 text-sm text-gray-900 outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/10 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                    >
                      {fontSizes.map(size => (
                        <option key={size.value} value={size.value}>
                          {size.name}
                        </option>
                      ))}
                    </select>
                    <input
                      type="number"
                      min="8"
                      max="200"
                      value={fontSize}
                      onChange={(e) => {
                        const value = parseInt(e.target.value);
                        setFontSize(value);
                        handlePropertyChange('fontSize', value);
                      }}
                      placeholder="Custom"
                      className="h-9 w-20 rounded-lg border border-gray-200 bg-gray-50 px-2.5 text-sm text-gray-900 outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/10 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                    />
                  </div>
                </div>

                {/* Font Weight */}
                <div className="mb-3">
                  <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">Weight</div>
                  <select
                    value={fontWeight}
                    onChange={async (e) => {
                      const value = parseInt(e.target.value, 10);
                      setFontWeight(value);
                      await handlePropertyChange('fontWeight', value);
                    }}
                    className="h-9 w-full cursor-pointer rounded-lg border border-gray-200 bg-gray-50 px-2.5 text-sm text-gray-900 outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/10 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                  >
                    {getAvailableWeights(fontFamily).map(weight => (
                      <option key={weight} value={weight}>
                        {getWeightName(weight)}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Text Alignment */}
                <div className="mb-3">
                  <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">Alignment</div>
                  <div className="flex gap-1.5">
                    <button
                      className={`flex h-9 w-9 items-center justify-center rounded-lg border transition-colors ${textAlign === 'left' ? 'border-primary bg-primary/10 text-primary' : 'border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700'}`}
                      onClick={() => {
                        setTextAlign('left');
                        handlePropertyChange('textAlign', 'left');
                      }}
                      title="Align Left"
                    >
                      <FaAlignLeft size={12} />
                    </button>
                    <button
                      className={`flex h-9 w-9 items-center justify-center rounded-lg border transition-colors ${textAlign === 'center' ? 'border-primary bg-primary/10 text-primary' : 'border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700'}`}
                      onClick={() => {
                        setTextAlign('center');
                        handlePropertyChange('textAlign', 'center');
                      }}
                      title="Align Center"
                    >
                      <FaAlignCenter size={12} />
                    </button>
                    <button
                      className={`flex h-9 w-9 items-center justify-center rounded-lg border transition-colors ${textAlign === 'right' ? 'border-primary bg-primary/10 text-primary' : 'border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700'}`}
                      onClick={() => {
                        setTextAlign('right');
                        handlePropertyChange('textAlign', 'right');
                      }}
                      title="Align Right"
                    >
                      <FaAlignRight size={12} />
                    </button>
                  </div>
                </div>

                {/* Line Height */}
                <div className="mb-0">
                  <div className="mb-1 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                    <span>Line Height</span>
                    <button
                      onClick={() => {
                        setLineHeight(1.16);
                        handlePropertyChange('lineHeight', 1.16);
                      }}
                      title="Reset to default (1.16)"
                      className="flex h-5 w-5 items-center justify-center rounded text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-300"
                    >
                      <span className="material-symbols-rounded text-[14px]">restart_alt</span>
                    </button>
                  </div>
                  <div className="py-1">
                    <input
                      type="range"
                      min="0.5"
                      max="3"
                      step="0.1"
                      value={lineHeight}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value);
                        setLineHeight(value);
                        handlePropertyChange('lineHeight', value);
                      }}
                      className={sliderClass}
                    />
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Image Properties */}
        {selectedObject.type === 'image' && selectedObject.name !== 'emoji' && (
          <div className="border-b border-gray-100 dark:border-gray-800">
            <div className="px-4 py-3">
              <div className="mb-3 text-[11px] font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">Image</div>
              {/* Image Preview */}
              {(() => {
                const imgSrc = (selectedObject as fabric.Image).getSrc?.()
                  || (selectedObject as any)._originalElement?.src
                  || (selectedObject as any).dataUrl
                  || (selectedObject as any).src;
                return imgSrc ? (
                  <div className="mb-3 overflow-hidden rounded-lg border border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800">
                    <img
                      src={imgSrc}
                      alt="Selected image"
                      className="block w-full h-auto"
                    />
                  </div>
                ) : null;
              })()}
              <div className="mb-3">
                <button
                  onClick={handleChangeImage}
                  className="flex h-9 w-full items-center justify-center gap-2 rounded-lg border border-gray-200 bg-gray-50 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  <FaImage size={12} />
                  Change Image
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />
              </div>

            </div>
          </div>
        )}

        {/* SVG Color Control */}
        {selectedObject.type === 'group' && (selectedObject as any).svgData && (
          <div className="border-b border-gray-100 dark:border-gray-800">
            <div className="px-4 py-3">
              <div className="mb-3 text-[11px] font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">SVG Color</div>
              <div className="mb-3">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <div
                      className="h-9 w-9 cursor-pointer rounded-lg border-2 border-gray-200 transition-all hover:scale-105 hover:border-primary dark:border-gray-700"
                      style={{ background: svgColor }}
                      onClick={() => {
                        const colorInput = document.getElementById('svgColorInput') as HTMLInputElement;
                        if (colorInput) colorInput.click();
                      }}
                    />
                    <input
                      id="svgColorInput"
                      type="color"
                      value={svgColor}
                      onChange={(e) => {
                        const newColor = e.target.value;
                        setSvgColor(newColor);
                        handleSvgColorChange({ target: { value: newColor } } as React.ChangeEvent<HTMLInputElement>);
                      }}
                      className="pointer-events-none absolute left-0 top-0 h-0 w-0 opacity-0"
                    />
                  </div>
                  <span className="font-mono text-xs font-medium text-gray-700 dark:text-gray-300">{svgColor.toUpperCase()}</span>
                </div>
                <div className="mt-2.5 flex flex-wrap gap-1.5">
                  {presetColors.map((color) => (
                    <button
                      key={color}
                      className={`h-5 w-5 cursor-pointer rounded transition-all hover:scale-110 ${svgColor === color ? 'ring-2 ring-primary ring-offset-1 dark:ring-offset-gray-900' : 'border border-gray-200 dark:border-gray-700'}`}
                      style={{ background: color }}
                      onClick={() => {
                        setSvgColor(color);
                        handleSvgColorChange({ target: { value: color } } as React.ChangeEvent<HTMLInputElement>);
                      }}
                      title={color.toUpperCase()}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Shape Properties - Fill and Stroke */}
        {isShape(selectedObject) && (
          <>
            <div className="border-b border-gray-100 dark:border-gray-800">
              <div className="px-4 py-3">
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-[11px] font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">Fill</span>
                  <button
                    onClick={handleShapeFillToggle}
                    title={shapeFillEnabled ? 'Disable fill' : 'Enable fill'}
                    className={`flex h-6 items-center rounded-md border px-2 text-[11px] font-medium transition-colors ${shapeFillEnabled ? 'border-primary/30 bg-primary/10 text-primary' : 'border-gray-200 bg-gray-50 text-gray-400 dark:border-gray-700 dark:bg-gray-800'}`}
                  >
                    {shapeFillEnabled ? 'On' : 'Off'}
                  </button>
                </div>
                {shapeFillEnabled && (
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <div
                        className="h-9 w-9 cursor-pointer rounded-lg border-2 border-gray-200 transition-all hover:scale-105 hover:border-primary dark:border-gray-700"
                        style={{ background: shapeFillColor }}
                        onClick={() => {
                          const colorInput = document.getElementById('shapeFillColorInput') as HTMLInputElement;
                          if (colorInput) colorInput.click();
                        }}
                      />
                      <input
                        id="shapeFillColorInput"
                        type="color"
                        value={shapeFillColor}
                        onChange={handleShapeFillColorChange}
                        className="pointer-events-none absolute left-0 top-0 h-0 w-0 opacity-0"
                      />
                    </div>
                    <input
                      type="text"
                      value={shapeFillColor.toUpperCase()}
                      onChange={handleShapeFillColorValueChange}
                      onBlur={handleShapeFillColorValueBlur}
                      placeholder="#FFFFFF"
                      maxLength={7}
                      className="h-8 w-24 rounded-md border border-gray-200 bg-gray-50 px-2 font-mono text-xs font-medium text-gray-700 outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/10 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300"
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="border-b border-gray-100 dark:border-gray-800">
              <div className="px-4 py-3">
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-[11px] font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">Stroke</span>
                  <button
                    onClick={handleShapeStrokeToggle}
                    title={shapeStrokeEnabled ? 'Disable stroke' : 'Enable stroke'}
                    className={`flex h-6 items-center rounded-md border px-2 text-[11px] font-medium transition-colors ${shapeStrokeEnabled ? 'border-primary/30 bg-primary/10 text-primary' : 'border-gray-200 bg-gray-50 text-gray-400 dark:border-gray-700 dark:bg-gray-800'}`}
                  >
                    {shapeStrokeEnabled ? 'On' : 'Off'}
                  </button>
                </div>
                {shapeStrokeEnabled && (
                  <>
                    <div className="mb-3 flex items-center gap-3">
                      <div className="relative">
                        <div
                          className="h-9 w-9 cursor-pointer rounded-lg border-2 border-gray-200 transition-all hover:scale-105 hover:border-primary dark:border-gray-700"
                          style={{ background: shapeStrokeColor }}
                          onClick={() => {
                            const colorInput = document.getElementById('shapeStrokeColorInput') as HTMLInputElement;
                            if (colorInput) colorInput.click();
                          }}
                        />
                        <input
                          id="shapeStrokeColorInput"
                          type="color"
                          value={shapeStrokeColor}
                          onChange={handleShapeStrokeColorChange}
                          className="pointer-events-none absolute left-0 top-0 h-0 w-0 opacity-0"
                        />
                      </div>
                      <input
                        type="text"
                        value={shapeStrokeColor.toUpperCase()}
                        onChange={handleShapeStrokeColorValueChange}
                        onBlur={handleShapeStrokeColorValueBlur}
                        placeholder="#000000"
                        maxLength={7}
                        className="h-8 w-24 rounded-md border border-gray-200 bg-gray-50 px-2 font-mono text-xs font-medium text-gray-700 outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/10 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300"
                      />
                    </div>
                    <div>
                      <div className="mb-1 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                        <span>Width</span>
                        <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-700 dark:bg-gray-800 dark:text-gray-300">{shapeStrokeWidth}px</span>
                      </div>
                      <div className="py-1">
                        <input
                          type="range"
                          min="1"
                          max="20"
                          step="1"
                          value={shapeStrokeWidth}
                          onChange={(e) => handleShapeStrokeWidthChange(e as any)}
                          className={sliderClass}
                        />
                        <div className="mt-0.5 flex justify-between text-[10px] text-gray-300 dark:text-gray-600">
                          <span>1px</span>
                          <span>20px</span>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>
      {/* Toast */}
      <div className="fixed right-5 top-5 z-[1000]">
        {toast && (
          <div className={`flex items-center gap-2 rounded-lg px-4 py-3 text-sm font-medium text-white shadow-lg animate-[slideIn_0.3s_ease] ${toast.type === 'success' ? 'bg-green-600' : 'bg-red-500'}`}>
            {toast.type === 'success' ? <span className="material-symbols-rounded text-[16px]">check_circle</span> : <span className="material-symbols-rounded text-[16px]">error</span>} {toast.message}
          </div>
        )}
      </div>
    </>
  );
};

export default PropertiesPanel;
